import styles from './importPricesPanel.module.css'
import { Action, Badge, Button, FieldList, FieldType, FieldValue, FileField, FileType, FormField, O, option, PanelLoader, useGetCrumbs } from '../../../../features/ui'
import { RoutePath, useRoutesHooks } from '../../../../features/routes'
import { useMemo, useState } from 'react'
import { toList } from '../../../../utils/listUtils'
import { useNavigate } from 'react-router-dom'
import { useActions } from './actions'
import { ImportedPricesReport } from './importedPricesReport'
import { CsvImportResult, ImportPriceField } from '../../../../features/importer'
import { PricesCsvSample } from './pricesCsvSample'
import { useDownloadPricesTemplate } from './useDownloadPricesTemplate'

export const ImportPricesPanel = () => {
    const priceFieldOptions = useMemo(() => [
        option(ImportPriceField.PURCHASE_PRICE, 'Precio de Compra'),
        option(ImportPriceField.PRICE_YIELD, 'Rentabilidad'),
        option(ImportPriceField.PRICE, 'Precio de Venta'),
        option(ImportPriceField.PRICE_VAT, 'Precio de Venta c/IVA')
    ], [])
    
    const [step, setStep] = useState(1)
    const [priceFieldValue, setPriceFieldValue] = useState<O<ImportPriceField>>()
    const [importFile, setImportFile] = useState<File>()
    const [result, setResult] = useState<O<CsvImportResult>>()
    
    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()
    const downloadPricesTemplate = useDownloadPricesTemplate()
    const { updatePrices } = useActions()

    const goToProducts = () => navigate(getRoutePath(RoutePath.PRODUCTS))

    const actions: Action[] = useMemo(() => [
        { label: 'Lista de Productos', kind: 'tertiary', onClick: goToProducts }
    ], [])

    const crumbs = useGetCrumbs(
        'Productos',
        RoutePath.PRODUCTS,
        undefined, undefined,
        'Actualizar Precios'
    )

    const goToStep = (newStep: number) => () => setStep(newStep)

    const renderStep = (
        n: number,
        description: string,
        children: JSX.Element
    ) => {
        const showPreviousBtn = n === step && n > 1 && n < 5
        const showNextBtn = n === step && n < 4 && (
            n === 1 ? !!priceFieldValue : (
                n === 3 ? !!importFile : true
            )
        )

        return (
            <div className={styles.stepContainer}>
                <div className={styles.stepHeader}>
                    <div className={styles.stepDescription}>
                        <Badge
                            label={`Paso ${n}`}
                            kind={n === step ? 'secondary' : 'primary'}
                            icon='arrow-right'
                        />
                        <span>{description}</span>
                    </div>
                    <div className={styles.stepArrows}>
                        {showPreviousBtn && (
                            <Button
                                icon="arrow-up"
                                kind="tertiary"
                                small
                                onClick={goToStep(n - 1)}
                            />
                        )}
                        {showNextBtn && (
                            <Button
                                icon="arrow-down"
                                kind="tertiary"
                                small
                                onClick={goToStep(n + 1)}
                            />
                        )}
                    </div>
                </div>
                {children}
            </div>
        )
    }

    const onChangePriceField = (value?: FieldValue) => {
        const newPriceFieldValue = value as O<ImportPriceField>
        setPriceFieldValue(newPriceFieldValue)
        newPriceFieldValue && setStep(2)
    }

    const priceFields = [
        {
            name: 'price-field',
            type: FieldType.SELECT,
            options: priceFieldOptions,
            value: priceFieldValue,
            disabled: step !== 1,
            narrow: true,
            onChange: onChangePriceField
        }
    ]

    const onDownloadTemplate = async() => {
        await downloadPricesTemplate(priceFieldValue!)
        setStep(3)
    }

    const templateField: FormField = {
        name: 'template',
        type: FieldType.FILE,
        render: () => (
            <div className={styles.templateField}>
                <PricesCsvSample priceField={priceFieldValue!} />
                <Button
                    icon="download"
                    label="Descargar"
                    disabled={step !== 2}
                    onClick={onDownloadTemplate}
                />
            </div>
        )
    }

    const onChangeImportFile = (value?: File) => {
        setImportFile(value)
        value && setStep(4)
    }

    const importField: FormField = {
        name: 'import',
        type: FieldType.FILE,
        label: ' ',
        labelSpace: 2,
        hint: 'Formato: CSV, XLS, XLSX',
        render: () => (
            <FileField
                id="import"
                fileTypes={[FileType.CSV, FileType.XLS, FileType.XLSX]}
                disabled={step !== 3}
                onChange={onChangeImportFile}
            />
        )
    }

    const onUpdatePrices = async () => {
        const newResult = await updatePrices(importFile!, priceFieldValue!)
        setResult(newResult)
        setStep(5)
    }

    const onNewUpdate = () => {
        setPriceFieldValue(undefined)
        setImportFile(undefined)
        setResult(undefined)
        setStep(1)
    }

    return (
        <PanelLoader
            title="Actualizar Lista de Precios"
            crumbs={crumbs}
            actions={actions}
            ready
        >
            {step !== 5 ? (
                <>
                    {renderStep(
                        1,
                        'Seleccionar el campo a actualizar.',
                        <FieldList fields={priceFields} />
                    )}
                    
                    {step > 1 && renderStep(
                        2,
                        'Descargar la plantilla del archivo.',
                        <FieldList fields={toList(templateField)} />
                    )}

                    {step > 2 && renderStep(
                        3,
                        'Editar el archivo descargado con los nuevos valores e importarlo.',
                        <FieldList fields={toList(importField)} />
                    )}

                    {step > 3 && importField && renderStep(
                        4,
                        'Actualizar los precios con los valores importados.',
                        <Button
                            icon="repeat"
                            label="Actualizar Precios"
                            disabled={step !== 4}
                            onClick={onUpdatePrices}
                        />
                    )}
                </>
            ) : (
                renderStep(
                    5,
                    'El archivo se importó correctamente.',
                    <ImportedPricesReport
                        csvImportResult={result!}
                        priceField={priceFieldValue!}
                        onNewUpdate={onNewUpdate}
                    />
                )
            )}
        </PanelLoader>
    )
}
